import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../../axiosBaseQuery";
import { SUPPORT_ENDPOINTS, DIAGNOSTIC_BUNDLE_ENDPOINTS } from "../../../../api/constants";

/**
 * RTK Query API поддержки EyeHub (Ц13).
 *
 * Тикеты (RBAC: hub-support-engineer видит все org, org-* — только свою),
 * комментарии (тред), вложения (требуют phi_ack), триаж (статус/критичность/SLA)
 * и диагностические бандлы.
 *
 * PHI НИКОГДА не попадает в EyeHub — свободный текст проходит серверный
 * phi_lint, а вложение регистрируется ССЫЛКОЙ (object_ref), файл в хаб не грузится.
 * Списки — UnifiedOffsetPagination ({results, count, pageIndex, pageCount, ...}).
 *
 * Контракты, на которых ломались прошлые версии портала:
 *   - `POST tickets/` требует `org` (uuid организации) — без него 400 на каждый submit;
 *   - вложение уходит на `tickets/{id}/attach/` JSON-телом
 *     `{filename, object_ref, phi_ack:true}` (НЕ FormData и НЕ `.../attachments/`);
 *   - `GET diagnostic-bundles/` отдаёт ТОЛЬКО одобренные бандлы: неодобренные
 *     скрыты queryset'ом бэкенда до `POST diagnostic-bundles/{id}/approve/`.
 */
export const supportApi = createApi({
  reducerPath: "supportApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Ticket", "TicketComment", "DiagnosticBundle"],
  endpoints: (builder) => ({
    getTickets: builder.query({
      query: ({
        limit = 20,
        offset = 0,
        status,
        severity,
        category,
        org,
        search,
        ordering,
      } = {}) => ({
        url: SUPPORT_ENDPOINTS.TICKETS,
        method: "GET",
        params: {
          limit,
          offset,
          ...(status ? { status } : {}),
          ...(severity ? { severity } : {}),
          ...(category ? { category } : {}),
          ...(org ? { org } : {}),
          ...(search ? { search } : {}),
          ...(ordering ? { ordering } : {}),
        },
      }),
      providesTags: [{ type: "Ticket", id: "LIST" }],
    }),

    getTicket: builder.query({
      query: (id) => ({ url: SUPPORT_ENDPOINTS.TICKET_DETAIL(id), method: "GET" }),
      providesTags: (result, error, id) => [
        { type: "Ticket", id },
        { type: "TicketComment", id: `LIST-${id}` },
      ],
    }),

    /**
     * Создание тикета. Обязательное поле — `org` (uuid); `site`/`installation`
     * кросс-валидируются бэкендом на принадлежность этой организации.
     */
    createTicket: builder.mutation({
      query: (data) => ({ url: SUPPORT_ENDPOINTS.TICKETS, method: "POST", data }),
      invalidatesTags: [{ type: "Ticket", id: "LIST" }],
    }),

    /**
     * Триаж тикета — ОДИН PATCH на все изменённые поля.
     * Принимает `{id, status?, severity?, sla_due_at?}` (TicketUpdateSerializer).
     */
    updateTicket: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: SUPPORT_ENDPOINTS.TICKET_DETAIL(id),
        method: "PATCH",
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Ticket", id },
        { type: "Ticket", id: "LIST" },
      ],
    }),

    getTicketComments: builder.query({
      query: (id) => ({ url: SUPPORT_ENDPOINTS.TICKET_COMMENTS(id), method: "GET" }),
      providesTags: (result, error, id) => [{ type: "TicketComment", id: `LIST-${id}` }],
    }),

    addTicketComment: builder.mutation({
      query: ({ id, body }) => ({
        url: SUPPORT_ENDPOINTS.TICKET_COMMENTS(id),
        method: "POST",
        data: { body },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "TicketComment", id: `LIST-${id}` },
        { type: "Ticket", id },
      ],
    }),

    /**
     * Регистрация внешнего артефакта у тикета (`POST tickets/{id}/attach/`).
     *
     * Хаб файлы не хранит — пишется только имя и ссылка на объект во внешнем
     * хранилище. `phi_ack: true` обязателен: без него бэкенд отвечает 400
     * (детективный PHI-контроль). Инвалидация тикета перечитывает вложения.
     */
    addTicketAttachment: builder.mutation({
      query: ({ id, filename, object_ref: objectRef, phi_ack: phiAck }) => ({
        url: SUPPORT_ENDPOINTS.TICKET_ATTACH(id),
        method: "POST",
        data: { filename, object_ref: objectRef, phi_ack: phiAck },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Ticket", id }],
    }),

    // === Диагностические бандлы (Ц13) ===

    getDiagnosticBundles: builder.query({
      query: ({ limit = 20, offset = 0, installation, ticket } = {}) => ({
        url: DIAGNOSTIC_BUNDLE_ENDPOINTS.LIST,
        method: "GET",
        params: {
          limit,
          offset,
          ...(installation ? { installation } : {}),
          ...(ticket ? { ticket } : {}),
        },
      }),
      providesTags: [{ type: "DiagnosticBundle", id: "LIST" }],
    }),

    /** Одобрение оператором — единственный способ сделать бандл читаемым. */
    approveDiagnosticBundle: builder.mutation({
      query: (id) => ({ url: DIAGNOSTIC_BUNDLE_ENDPOINTS.APPROVE(id), method: "POST" }),
      invalidatesTags: [{ type: "DiagnosticBundle", id: "LIST" }],
    }),

    deleteDiagnosticBundle: builder.mutation({
      query: (id) => ({ url: DIAGNOSTIC_BUNDLE_ENDPOINTS.DETAIL(id), method: "DELETE" }),
      invalidatesTags: [{ type: "DiagnosticBundle", id: "LIST" }],
    }),
  }),
});

export const {
  useGetTicketsQuery,
  useGetTicketQuery,
  useCreateTicketMutation,
  useUpdateTicketMutation,
  useGetTicketCommentsQuery,
  useAddTicketCommentMutation,
  useAddTicketAttachmentMutation,
  useGetDiagnosticBundlesQuery,
  useApproveDiagnosticBundleMutation,
  useDeleteDiagnosticBundleMutation,
} = supportApi;

export default supportApi;
